import React, { useState, useEffect } from 'react';
import { Box, TextField, useMediaQuery, useTheme, Chip, Avatar, Button } from '@mui/material';
import { DialogueModal } from '../DialogueModal/DialogueModal';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IUserSummary } from '../../types/global/helper';
import { IHost, IPartner } from '../../redux/discovery/types';
import CustomButton from '../Button/CustomButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { PeopleSearch } from '../PeopleSearch/PeopleSearch';
import { getJWtDetails, useDispatchPromise } from '../../utils/AppUtils';
import { initiateMessageForRoom } from '../../redux/messages/actions';

export interface IMessageDialogueProps {
    open: boolean;
    onClose: () => void;
    recipient?: IUserSummary;
    roleId?: string;
    auditionId?: string;
    fromModal?: boolean;
}

const MessageDialogue: React.FC<IMessageDialogueProps> = ({
    open,
    onClose,
    recipient,
    roleId,
    auditionId,
    fromModal,
}) => {
    const muiTheme = useTheme();
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedRecipients, setSelectedRecipients] = useState<IUserSummary[]>([]);
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    const { themeStyle } = useThemeContext();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('xs'));
    const { user_id } = getJWtDetails();
    const dispatch = useDispatchPromise();

    useEffect(() => {
        if (recipient) {
            setSelectedRecipients([recipient]);
        }
    }, [recipient]);

    const handleClose = () => {
        setMessage('');
        onClose();
        setLoading(false);
    };

    const handleSubmit = () => {
        if (selectedRecipients.length > 0 && message.trim()) {
            const payload = {
                message_content: message,
                receiver: selectedRecipients.map(user => user._id),
                sender: user_id,
                from_modal: fromModal,
                ...(auditionId && { audition: auditionId }),
                ...(roleId && { role: roleId }),
            };
            setLoading(true);
            dispatch(initiateMessageForRoom(payload))
                .then(() => {
                    handleClose();
                })
                .catch(e => {
                    handleClose();
                });
        }
    };

    const handleRecipientChange = (users: IUserSummary[] | IHost | IPartner[] | undefined) => {
        if (Array.isArray(users)) {
            setSelectedRecipients(users as IUserSummary[]);
        } else if (users) {
            // If it's a single IHost or IPartner, convert it to IUserSummary array
            const userSummary: IUserSummary = {
                _id: (users as IHost).id,
                id: (users as IHost).id,
                firstName: (users as IHost).name || '',
                lastName: '',
                userName: (users as IHost).name || '',
                profile_picture: (users as IHost).profile_picture || '',
                verified: false,
                role: 'host',
                audme_profile: '',
            };
            setSelectedRecipients([userSummary]);
        }
        setSearchDialogOpen(false);
    };

    const handleDeleteRecipient = (recipientToDelete: IUserSummary) => {
        setSelectedRecipients(prev => prev.filter(r => r._id !== recipientToDelete._id));
    };

    const isButtonEnabled = selectedRecipients.length > 0 && message.trim().length > 0;

    return (
        <>
            <DialogueModal
                open={open}
                onClose={onClose}
                buttonEnabled={false}
                header="Send Message"
                paperSX={{
                    height: { xs: '100dvh', sm: 'fit-content' },
                    width: '100vw',
                    maxWidth: {
                        xs: '90dvw',
                        sm: '500px',
                    },
                    maxHeight: {
                        xs: '70%',
                        sm: '100%',
                    },
                }}
                dialogueProps={{
                    fullWidth: true,
                    open: open,
                    maxWidth: 'xs',
                    fullScreen: isMobile,
                }}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Recipients Display with Add Button */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            minHeight: '56px',
                            width: '100%',
                            p: 1,
                            border: `1px solid ${theme[themeStyle].grey}`,
                            borderRadius: 1,
                            alignItems: 'center',
                        }}>
                        {selectedRecipients.map((recipient, index) => (
                            <Chip
                                key={index}
                                avatar={<Avatar src={recipient.profile_picture} />}
                                label={`${recipient.firstName} ${recipient.lastName}`}
                                onDelete={() => handleDeleteRecipient(recipient)}
                                deleteIcon={<CloseIcon />}
                                sx={{
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                    color: theme[themeStyle].tag_white,
                                    '& .MuiChip-deleteIcon': {
                                        color: theme[themeStyle].tag_white,
                                        '&:hover': {
                                            color: theme[themeStyle].primary_red,
                                        },
                                    },
                                }}
                            />
                        ))}
                        <Button
                            onClick={() => setSearchDialogOpen(true)}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                minWidth: '5px',
                                color: theme[themeStyle].highlight,
                                '&:hover': {
                                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                },
                            }}>
                            <AddIcon />
                        </Button>
                    </Box>

                    {/* Message Body */}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Message"
                        multiline
                        rows={8}
                        variant="outlined"
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: 'gray' },
                                '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                '&.Mui-focused fieldset': { borderColor: theme[themeStyle].highlight },
                            },
                            '& .MuiInputLabel-root': { color: 'gray' },
                            '& .MuiInputLabel-root.Mui-focused': { color: theme[themeStyle].highlight },
                            '& .MuiInputBase-input::placeholder': {
                                color: 'lightgray',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                        }}
                        InputProps={{
                            style: {
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 14,
                                color: theme[themeStyle].tag_white,
                            },
                        }}
                    />

                    {/* Send Button */}
                    <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                        <CustomButton
                            label="Send Message"
                            onClick={handleSubmit}
                            loading={loading}
                            disabled={!isButtonEnabled}
                            customStyle={{
                                borderRadius: '5px',
                                opacity: isButtonEnabled ? 1 : 0.5,
                                width: '100%',
                            }}
                        />
                    </Box>
                </Box>
            </DialogueModal>

            {/* Search Dialog */}
            <DialogueModal
                open={searchDialogOpen}
                onClose={() => setSearchDialogOpen(false)}
                buttonEnabled={false}
                header="Select Recipients"
                paperSX={{
                    height: { xs: '100dvh', sm: '70dvh' },
                    width: '100vw',
                    maxWidth: {
                        xs: '90dvw',
                        sm: '500px',
                    },
                    maxHeight: {
                        xs: '70%',
                        sm: '100%',
                    },
                }}
                headerStyle={{ marginBottom: 1.5 }}
                dialogueProps={{
                    fullWidth: true,
                    open: searchDialogOpen,
                    maxWidth: 'xs',
                    fullScreen: isMobile,
                }}>
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden', // Prevent outer container from scrolling
                    }}>
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto', // Make this box scrollabl
                        }}>
                        <PeopleSearch
                            value={selectedRecipients}
                            onChange={handleRecipientChange}
                            placeholder="Search people by name or username..."
                            withProfileSearch
                            strict
                        />
                    </Box>
                </Box>
            </DialogueModal>
        </>
    );
};

export { MessageDialogue };
